import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { 
  TrophyIcon, 
  UsersIcon, 
  CurrencyDollarIcon, 
  ClockIcon,
  CalendarIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/LoadingSpinner'
import PoolsApi, { Pool } from '../services/poolsApi'
import { isPoolAdmin } from '../utils/roles'

export default function PoolsListPage() {
  const auth0 = useAuth0()
  const { user, getAccessTokenSilently } = auth0
  const [pools, setPools] = useState<Pool[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const userIsPoolAdmin = isPoolAdmin(user)
  const poolsApi = new PoolsApi(auth0)

  useEffect(() => {
    fetchPools()
  }, [])

  const fetchPools = async () => {
    try {
      setLoading(true)
      const data = await poolsApi.getPools()
      setPools(data.pools || [])
    } catch (err) {
      console.error('Error fetching pools:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch pools')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'completed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'football':
        return '🏈'
      case 'horse-racing':
        return '🏇'
      case 'cornhole':
        return '🌽'
      default:
        return '🎯'
    }
  }

  const isDeadlineSoon = (pickDeadline?: string) => {
    if (!pickDeadline) return false
    const deadline = new Date(pickDeadline)
    const now = new Date()
    const hoursUntilDeadline = (deadline.getTime() - now.getTime()) / (1000 * 60 * 60)
    return hoursUntilDeadline > 0 && hoursUntilDeadline <= 24 // Within 24 hours
  }

  const isDeadlinePassed = (pickDeadline?: string) => {
    if (!pickDeadline) return false
    return new Date() > new Date(pickDeadline)
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Game Pools</h1>
            <p className="mt-1 text-lg text-gray-600 dark:text-gray-300">
              Join or manage game pools for football, horse racing, and cornhole
            </p>
          </div>
          
          {userIsPoolAdmin && (
            <Link
              to="/admin/pools/football/create"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <TrophyIcon className="w-4 h-4 mr-2" />
              Create Pool
            </Link>
          )}
        </div>
      </div>

      {/* Pools Grid */}
      {pools.length === 0 ? (
        <div className="text-center py-12">
          <TrophyIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No pools available</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Check back later or create a new pool to get started.
          </p>
          {userIsPoolAdmin && (
            <Link
              to="/admin/pools/football/create"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <TrophyIcon className="w-5 h-5 mr-2" />
              Create Your First Pool
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pools.map((pool) => (
            <div
              key={pool.poolId}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{getTypeIcon(pool.type)}</span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {pool.name}
                      </h3>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(pool.status)}`}>
                        {pool.status.charAt(0).toUpperCase() + pool.status.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {pool.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                    {pool.description}
                  </p>
                )}

                {/* Pool Stats */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center text-gray-500 dark:text-gray-400">
                      <UsersIcon className="w-4 h-4 mr-1" />
                      Participants
                    </div>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {pool.currentParticipants}/{pool.maxParticipants}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center text-gray-500 dark:text-gray-400">
                      <CurrencyDollarIcon className="w-4 h-4 mr-1" />
                      Entry Fee
                    </div>
                    <span className="text-gray-900 dark:text-white font-medium">
                      ${pool.entryFee}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center text-gray-500 dark:text-gray-400">
                      <CalendarIcon className="w-4 h-4 mr-1" />
                      Created
                    </div>
                    <span className="text-gray-900 dark:text-white">
                      {formatDate(pool.createdAt)}
                    </span>
                  </div>

                  {/* Pick Deadline */}
                  {pool.pickDeadline && (
                    <div className={`flex items-center justify-between text-sm p-2 rounded-lg ${
                      isDeadlinePassed(pool.pickDeadline)
                        ? 'bg-red-50 dark:bg-red-900/20'
                        : isDeadlineSoon(pool.pickDeadline)
                        ? 'bg-yellow-50 dark:bg-yellow-900/20'
                        : 'bg-blue-50 dark:bg-blue-900/20'
                    }`}>
                      <div className={`flex items-center ${
                        isDeadlinePassed(pool.pickDeadline)
                          ? 'text-red-600 dark:text-red-400'
                          : isDeadlineSoon(pool.pickDeadline)
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-blue-600 dark:text-blue-400'
                      }`}>
                        {isDeadlinePassed(pool.pickDeadline) ? (
                          <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                        ) : (
                          <ClockIcon className="w-4 h-4 mr-1" />
                        )}
                        Pick Deadline
                      </div>
                      <span className={`font-medium ${
                        isDeadlinePassed(pool.pickDeadline)
                          ? 'text-red-700 dark:text-red-300'
                          : isDeadlineSoon(pool.pickDeadline)
                          ? 'text-yellow-700 dark:text-yellow-300'
                          : 'text-blue-700 dark:text-blue-300'
                      }`}>
                        {formatDateTime(pool.pickDeadline)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Action Button */}
                <Link
                  to={`/pools/${pool.poolId}`}
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  View Pool
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Admin Quick Actions */}
      {userIsPoolAdmin && (
        <div className="mt-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Pool Administration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/admin/pools/football"
              className="flex flex-col items-center p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
            >
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center mb-3 group-hover:bg-green-200 dark:group-hover:bg-green-900/40 transition-colors">
                <TrophyIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white text-center">Manage Football Pools</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">View and manage existing football pools</p>
            </Link>

            <Link
              to="/admin/pools/football/create"
              className="flex flex-col items-center p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
            >
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center mb-3 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/40 transition-colors">
                <TrophyIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white text-center">Create Football Pool</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">Set up a new football pool</p>
            </Link>

            <Link
              to="/admin/pools/horse-racing"
              className="flex flex-col items-center p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
            >
              <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg flex items-center justify-center mb-3 group-hover:bg-yellow-200 dark:group-hover:bg-yellow-900/40 transition-colors">
                <span className="text-2xl">🐎</span>
              </div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white text-center">Manage Horse Racing Pools</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">View and manage horse racing pools</p>
            </Link>

            <Link
              to="/admin/pools/horse-racing/create"
              className="flex flex-col items-center p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
            >
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center mb-3 group-hover:bg-orange-200 dark:group-hover:bg-orange-900/40 transition-colors">
                <span className="text-2xl">🏇</span>
              </div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white text-center">Create Horse Racing Pool</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">Set up a new horse racing pool</p>
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
